import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MailOpen, Trash2, ShieldAlert, Paperclip, Phone } from "lucide-react";
import { ModulePanel } from "@/components/admin/AdminPrimitives";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useMessagesContact,
  useMarquerLuMessageContact,
  useDeleteMessageContact,
} from "@/hooks/useMessageContact";
import {
  useDemandesVerification,
  useMarquerLueDemandeVerification,
  useDeleteDemandeVerification,
} from "@/hooks/useDemandeVerification";
import type { MessageContactDto } from "@/types/message-contact.types";
import type { DemandeVerificationDto, DemandeVerificationAttachmentDto } from "@/types/demande-verification.types";

/** Aperçu d'une pièce jointe, affiché dans la popup de détail — jamais un lien
 * externe : image/vidéo/audio rendus en ligne, document intégré via iframe
 * pour rester dans l'application. */
function AttachmentPreview({ piece }: { piece: DemandeVerificationAttachmentDto }) {
  if (piece.type === "image") {
    return (
      <img src={piece.url} alt="Pièce jointe" className="max-h-72 w-full rounded-md border border-border object-contain" />
    );
  }
  if (piece.type === "video") {
    return <video controls src={piece.url} className="max-h-72 w-full rounded-md border border-border" />;
  }
  if (piece.type === "audio") {
    return <audio controls src={piece.url} className="w-full" />;
  }
  return (
    <div className="overflow-hidden rounded-md border border-border">
      <iframe title="Document" src={piece.url} className="h-72 w-full bg-white" />
    </div>
  );
}

/** Panneaux d'"inbox" partagés entre la page Messages (vue dédiée) et le
 * tableau de bord (dans une popup) : une seule implémentation de chaque
 * liste pour rester cohérent entre les deux emplacements. */

// ─── Demandes de vérification (signalements publics) ─────────────────────────
export function DemandesVerificationPanel() {
  const { data, isLoading } = useDemandesVerification();
  const marquerLue = useMarquerLueDemandeVerification();
  const deleteDemande = useDeleteDemandeVerification();
  const [deleteTarget, setDeleteTarget] = useState<DemandeVerificationDto | null>(null);
  const [detail, setDetail] = useState<DemandeVerificationDto | null>(null);

  const demandes: DemandeVerificationDto[] = Array.isArray(data) ? data : [];

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteDemande.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: () => {
        toast.error("Impossible de supprimer cette demande");
        setDeleteTarget(null);
      },
    });
  };

  const openDetail = (d: DemandeVerificationDto) => {
    setDetail(d);
    if (!d.lu) marquerLue.mutate(d.id);
  };

  return (
    <>
      <ModulePanel title={`Demandes de vérification (${demandes.length})`} description="Signalements publics soumis depuis le site, sans compte">
        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Chargement…
          </div>
        ) : demandes.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Aucune demande reçue pour le moment</div>
        ) : (
          <div className="space-y-2">
            {demandes.map((d) => (
              <div
                key={d.id}
                onClick={() => openDetail(d)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && openDetail(d)}
                className={`cursor-pointer rounded-md border px-4 py-3 transition-colors hover:border-verified/60 ${d.lu ? "border-border bg-surface/40" : "border-verified/40 bg-verified/5"}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {d.souhaiteRecontact && (
                        <span className="inline-flex items-center gap-1 rounded-md bg-verified/10 px-1.5 py-0.5 text-[10px] font-semibold text-verified">
                          <Phone className="h-3 w-3" /> Souhaite être recontacté(e)
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 line-clamp-2 whitespace-pre-wrap text-sm text-foreground/90">{d.texte}</p>
                    {(d.nom || d.email || d.telephone) && (
                      <div className="mt-1.5 text-xs text-muted-foreground">
                        {[d.nom, d.email, d.telephone].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    {d.pieces.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {d.pieces.map((p) => (
                          <span
                            key={p.s3Key}
                            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-foreground"
                          >
                            <Paperclip className="h-3 w-3" /> {p.type}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-1.5 text-[11px] text-muted-foreground">
                      {new Date(d.creeLe).toLocaleString("fr-FR")}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!d.lu && (
                      <button
                        onClick={(e) => { e.stopPropagation(); marquerLue.mutate(d.id); }}
                        title="Marquer comme lu"
                        className="rounded p-1.5 text-muted-foreground hover:text-verified hover:bg-verified/10 transition-colors"
                      >
                        <MailOpen className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(d); }}
                      title="Supprimer"
                      className="rounded p-1.5 text-muted-foreground hover:text-alert hover:bg-alert/10 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ModulePanel>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Supprimer cette demande ?"
        description="Ce signalement sera définitivement supprimé."
        onConfirm={confirmDelete}
        loading={deleteDemande.isPending}
      />

      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>Demande de vérification</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {detail.souhaiteRecontact && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-verified/10 px-1.5 py-0.5 text-[10px] font-semibold text-verified">
                    <Phone className="h-3 w-3" /> Souhaite être recontacté(e)
                  </span>
                )}
                <p className="whitespace-pre-wrap text-sm text-foreground/90">{detail.texte}</p>

                {(detail.nom || detail.email || detail.telephone) && (
                  <div className="space-y-1 rounded-md border border-border bg-surface/40 px-3 py-2 text-xs">
                    {detail.nom && <div><span className="text-muted-foreground">Nom : </span>{detail.nom}</div>}
                    {detail.email && <div><span className="text-muted-foreground">Email : </span>{detail.email}</div>}
                    {detail.telephone && <div><span className="text-muted-foreground">Téléphone : </span>{detail.telephone}</div>}
                  </div>
                )}

                {detail.pieces.length > 0 && (
                  <div className="space-y-3">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Pièces jointes ({detail.pieces.length})
                    </div>
                    {detail.pieces.map((p) => (
                      <AttachmentPreview key={p.s3Key} piece={p} />
                    ))}
                  </div>
                )}

                <div className="text-[11px] text-muted-foreground">
                  Reçue le {new Date(detail.creeLe).toLocaleString("fr-FR")}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Messages de contact ──────────────────────────────────────────────────────
export function MessagesContactPanel() {
  const { data, isLoading } = useMessagesContact();
  const marquerLu = useMarquerLuMessageContact();
  const deleteMessage = useDeleteMessageContact();
  const [deleteTarget, setDeleteTarget] = useState<MessageContactDto | null>(null);
  const [detail, setDetail] = useState<MessageContactDto | null>(null);

  const messages: MessageContactDto[] = Array.isArray(data) ? data : [];

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteMessage.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: () => {
        toast.error("Impossible de supprimer ce message");
        setDeleteTarget(null);
      },
    });
  };

  const openDetail = (m: MessageContactDto) => {
    setDetail(m);
    if (!m.lu) marquerLu.mutate(m.id);
  };

  return (
    <>
      <ModulePanel title={`Messages de contact (${messages.length})`}>
        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Chargement…
          </div>
        ) : messages.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Aucun message reçu pour le moment</div>
        ) : (
          <div className="space-y-2">
            {messages.map((m) => (
              <div
                key={m.id}
                onClick={() => openDetail(m)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && openDetail(m)}
                className={`cursor-pointer rounded-md border px-4 py-3 transition-colors hover:border-verified/60 ${m.lu ? "border-border bg-surface/40" : "border-verified/40 bg-verified/5"}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-foreground">{m.nom || m.email}</span>
                      <span className="text-xs text-muted-foreground">{m.email}</span>
                      {m.confidentiel && (
                        <span className="inline-flex items-center gap-1 rounded-md bg-alert/10 px-1.5 py-0.5 text-[10px] font-semibold text-alert">
                          <ShieldAlert className="h-3 w-3" /> Confidentiel
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 line-clamp-2 whitespace-pre-wrap text-sm text-foreground/90">{m.message}</p>
                    <div className="mt-1.5 text-[11px] text-muted-foreground">
                      {new Date(m.creeLe).toLocaleString("fr-FR")}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!m.lu && (
                      <button
                        onClick={(e) => { e.stopPropagation(); marquerLu.mutate(m.id); }}
                        title="Marquer comme lu"
                        className="rounded p-1.5 text-muted-foreground hover:text-verified hover:bg-verified/10 transition-colors"
                      >
                        <MailOpen className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(m); }}
                      title="Supprimer"
                      className="rounded p-1.5 text-muted-foreground hover:text-alert hover:bg-alert/10 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ModulePanel>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Supprimer ce message ?"
        description={deleteTarget ? `Le message de « ${deleteTarget.nom || deleteTarget.email} » sera définitivement supprimé.` : undefined}
        onConfirm={confirmDelete}
        loading={deleteMessage.isPending}
      />

      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>{detail.nom || detail.email}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <span className="text-muted-foreground">{detail.email}</span>
                  {detail.confidentiel && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-alert/10 px-1.5 py-0.5 text-[10px] font-semibold text-alert">
                      <ShieldAlert className="h-3 w-3" /> Confidentiel
                    </span>
                  )}
                </div>
                <p className="whitespace-pre-wrap text-sm text-foreground/90">{detail.message}</p>
                <div className="text-[11px] text-muted-foreground">
                  Reçu le {new Date(detail.creeLe).toLocaleString("fr-FR")}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
